<script setup lang="ts">
import {Backing, BackingInterviewStep, BackingInvoiceStep, BackingStep} from "@/backing/interfaces/backing";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import {computed, onMounted, ref} from "vue";
import BackingInvoiced from "@/backing/components/BackingInvoiced.vue";
import Customer from "@/customer/interfaces/Customer";
import MarkdownView from "@/core/components/markdown/Markdown.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {attachmentApi, programAttachmentApi} from "@/container";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import {Attachment} from "@/attachment/interfaces/attachment";
import BackingReports from "@/backing/components/reports/BackingReports.vue";
import BackingInterviews from "@/backing/components/interviews/BackingInterviews.vue";
import * as Sentry from "@sentry/vue";
import {backingExtension} from "@/backing/extensions";
import useExperimentalFeatures from "@/core/composables/experimental-features/useExperimentalFeatures";
import FeatureWishlistSection from "@/core/components/sections/FeatureWishlistSection.vue";

const props = defineProps<{
    backing: Backing,
    customerId: number;
    consumer: Customer | Beneficiary;
    attachments?: Attachment[];
    programId: number;
}>();

const complianceAttachment = ref<Attachment | null>(null);
const operationSection = ref<HTMLElement | null>(null);
const {enabled: experimentalFeatureEnabled} = useExperimentalFeatures();

/**
 * The component the program substitutes for the generic body, if it has one.
 *
 * Looked up by slug so that this module stays agnostic: a program whose course
 * lives outside the backing engine registers itself, it is not named here.
 */
const extension = computed(() => backingExtension(props.backing.slug));

/**
 * Compute the billing placeholder replacement.
 */
const billing = computed(() =>
    [props.backing.first_year_billing_act?.code, props.backing.next_years_billing_act?.code]
        .filter(n => n)
        .join(' ou ')
);

/**
 * Check if the backing has completed steps
 */
const completedSteps = computed(() => props.backing.steps.filter((step: BackingStep) => step.completed_at));

/**
 * Get the interview steps
 */
const interviewSteps = computed(() => props.backing.steps.filter((step: BackingStep): step is BackingInterviewStep => {
    return step.resource_type === 'interview';
}));

/**
 * Flatten the interview instances.
 */
const interviewInstances = computed(() => {
    return interviewSteps.value.flatMap((step: BackingInterviewStep) => {
        return step.interview_instances?.map(instance => {
            return {
                instance,
                step: {
                    ...step,
                    interview_instances: undefined
                }
            };
        }) || [];
    });
});

/**
 * Get the completed invoice steps
 */
const completedInvoiceSteps = computed(() => completedSteps.value.filter((step: BackingStep): step is BackingInvoiceStep => {
    return step.resource_type === 'invoice';
}));

onMounted(() => {
    fetchComplianceAttachment();
});

/**
 * Get compliance attachment of the program if there is any.
 */
const fetchComplianceAttachment = () => {
    programAttachmentApi().programIndex(props.programId, {tags: ['compliance']})
        .then(response => {
            if (response?.length) {
                complianceAttachment.value = response[0];
            }
        });
};

/**
 * Scroll down to the section explaining how the backing works.
 */
const scrollToOperation = () => {
    operationSection.value?.scrollIntoView({behavior: 'smooth', block: 'start'});
};

/**
 * Print the compliance document.
 */
const printCompliance = () => {
    if (complianceAttachment.value) {
        attachmentApi().prefill(complianceAttachment.value.id, props.consumer.consumer_key)
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response], {type: response.type}));
                const printWindow = window.open(url);

                if (printWindow) {
                    printWindow.onload = () => {
                        try {
                            printWindow.print();
                        } catch (e) {
                            Sentry.captureException(e);
                        }
                    };
                }
            });
    }
};
</script>

<template>
  <div class="space-y-4">
    <BaseCard>
      <BaseCardBody>
        <div class="flex justify-space-between items-center mb-4">
          <div class="flex lg:flex-row flex-col lg:items-center gap-4">
            <h2 class="flex-1">
              {{ backing.name }}
            </h2>
            <BaseButton
              v-if="complianceAttachment" density="comfortable" prepend-icon="mdi-printer"
              @click="printCompliance"
            >
              Imprimer le {{ complianceAttachment.title }}
            </BaseButton>
          </div>
          <button
            v-if="backing.description" type="button" class="text-primary"
            @click="scrollToOperation"
          >
            <span class="underline">Comment ça marche ?</span>
            <span> 🧐</span>
          </button>
        </div>
        <div v-if="extension">
          <component
            :is="extension" v-if="experimentalFeatureEnabled"
            :consumer-key="consumer.consumer_key"
          />
          <div v-else class="text-center flex flex-col gap-2">
            <p class="text-center">
              ⏳ <span class="italic">En cours de développement</span>
            </p>
            <FeatureWishlistSection />
          </div>
        </div>
        <BackingInterviews
          v-else
          :steps="interviewSteps"
          :backing-id="backing.id"
          :consumer-key="consumer.consumer_key"
          :customer-id="customerId"
          :consumer="consumer"
        />
        <div v-if="backing.description" ref="operationSection" class="mt-4">
          <h3 class="text-lg font-medium">
            Fonctionnement
          </h3>
          <markdown-view
            class="text-gray-600"
            :content="backing.description.replace('{billing_code}', billing)"
          />
        </div>
        <div
          v-if="interviewInstances?.length && completedInvoiceSteps?.length"
          class="p-4 mt-2"
        >
          <div class="flex flex-row flex-wrap gap-4">
            <div v-for="(step, index) in completedInvoiceSteps" :key="'invoiced' + index">
              <BackingInvoiced :step="step" />
            </div>
          </div>
        </div>
      </BaseCardBody>
    </BaseCard>
    <BackingReports
      :backing="backing"
      :consumer-key="consumer.consumer_key"
      :interview-instances="interviewInstances?.map(instance => instance.instance) ?? null"
    />
  </div>
</template>
